import { useMemo, useState } from 'react';
import {
  useApplyReferralSubscriptionCredit,
  useReferralActivity,
  useReferralEarnings,
  useReferralHistory,
  useBusiness,
  useReferralSummary,
  useUser,
  useWithdrawReferralEarnings,
} from '@/api';
import {
  formatSettlementAccountLabel,
  getBusinessSettlementAccount,
} from '@/lib/helpers';
import { PageLoader } from '@/components/atoms';
import { formatCurrency } from '@/lib/helpers/format-currency';
import { History, Plus } from 'lucide-react';
import {
  ActivityPanel,
  ApplySubscriptionDialog,
  BalanceHero,
  EarningsSheet,
  HowItWorks,
  ReferralsTable,
  ShareSheet,
  TierProgress,
  WithdrawDialog,
} from './components';
import { REFERRAL_FOMO_MESSAGES } from './constants';
import './refer-earn.css';

export const ReferAndEarnPage = () => {
  const [shareOpen, setShareOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [earningsOpen, setEarningsOpen] = useState(false);
  const [fomoIdx] = useState(0);

  const { data: user } = useUser('me');
  const { data: business } = useBusiness();
  const { data: summary, isLoading: isSummaryLoading } = useReferralSummary();
  const { data: activity = [], isLoading: isActivityLoading } =
    useReferralActivity();
  const { data: history, isLoading: isHistoryLoading } = useReferralHistory();
  const { data: earningsView } = useReferralEarnings();
  const earnings = earningsView?.entries ?? [];

  const referrals = history?.referrals ?? [];
  const hasReferrals = referrals.length > 0;
  const paidCount = history?.completed.length ?? 0;

  const withdrawMutation = useWithdrawReferralEarnings(() =>
    setWithdrawOpen(false)
  );
  const applyMutation = useApplyReferralSubscriptionCredit(() =>
    setApplyOpen(false)
  );

  const isLoading = isSummaryLoading || isActivityLoading || isHistoryLoading;

  const bankLabel = useMemo(
    () =>
      formatSettlementAccountLabel(
        getBusinessSettlementAccount(business, user)
      ),
    [business, user]
  );

  const pendingPayout = useMemo(() => {
    if (!summary) return 0;
    return Math.max(0, summary.totalEarnings - summary.availableBalance);
  }, [summary]);

  const vendorName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ')
    : 'You';

  const fomoMessage = useMemo(() => {
    const latest = activity[0];
    if (latest?.message) return latest.message;
    return REFERRAL_FOMO_MESSAGES[fomoIdx % REFERRAL_FOMO_MESSAGES.length];
  }, [activity, fomoIdx]);

  const pageTitle = hasReferrals
    ? 'Your hustle is paying back'
    : 'Earn up to ₦10,500 per friend you bring';

  if (isLoading || !summary || !history) {
    return (
      <PageLoader message="Loading referral program..." className="py-20" />
    );
  }

  return (
    <div className="refer-earn-page flex flex-col gap-6">
      <div className="page-head">
        <div>
          <div className="rne-meta">Refer &amp; Earn</div>
          <h1 className="rne-h1">{pageTitle}</h1>
        </div>
        <button
          type="button"
          className="btn-outline-rne"
          onClick={() => setEarningsOpen(true)}
        >
          <History className="size-4" />
          Earnings history
        </button>
      </div>

      <BalanceHero
        summary={summary}
        referrals={referrals}
        hasReferrals={hasReferrals}
        pendingPayout={pendingPayout}
        onWithdraw={() => setWithdrawOpen(true)}
        onApplyToPlan={() => setApplyOpen(true)}
        onShare={() => setShareOpen(true)}
      />

      {hasReferrals && <TierProgress paidCount={paidCount} />}

      <div
        className="rne-section-h"
        style={{ marginTop: hasReferrals ? 18 : 0 }}
      >
        <div className="left">
          <h3 className="rne-h2">
            {hasReferrals ? "What's happening" : 'How it works'}
          </h3>
          <p className="rne-meta">
            {hasReferrals
              ? 'Activity from across the Sellyt vendor network'
              : 'Four steps from share to payout'}
          </p>
        </div>
      </div>

      {hasReferrals ? (
        <ActivityPanel
          activity={activity}
          stats={history.stats}
          vendorName={vendorName}
          fomoMessage={fomoMessage}
          onShare={() => setShareOpen(true)}
        />
      ) : (
        <HowItWorks />
      )}

      <div className="rne-section-h">
        <div className="left">
          <h3 className="rne-h2">Your referrals</h3>
          <p className="rne-meta">
            {hasReferrals
              ? `${referrals.length} people, ${formatCurrency(summary.totalEarnings)} earned`
              : 'Nobody yet: share your link to get started'}
          </p>
        </div>
        {hasReferrals && (
          <button
            type="button"
            className="btn-mint btn-sm"
            onClick={() => setShareOpen(true)}
          >
            <Plus className="size-4" />
            Refer someone
          </button>
        )}
      </div>

      <ReferralsTable
        referrals={referrals}
        onShare={() => setShareOpen(true)}
      />

      <ShareSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        referralLink={summary.referralLink}
      />

      <WithdrawDialog
        open={withdrawOpen}
        onOpenChange={setWithdrawOpen}
        availableBalance={summary.availableBalance}
        bankLabel={bankLabel}
        onConfirm={(amount) => withdrawMutation.mutate({ amount })}
        isSubmitting={withdrawMutation.isPending}
      />

      <ApplySubscriptionDialog
        open={applyOpen}
        onOpenChange={setApplyOpen}
        availableBalance={summary.availableBalance}
        planPrice={15000}
        onConfirm={(amount) => applyMutation.mutate({ amount })}
        isSubmitting={applyMutation.isPending}
      />

      <EarningsSheet
        open={earningsOpen}
        onOpenChange={setEarningsOpen}
        entries={earnings}
        totalEarnings={summary.totalEarnings}
        availableBalance={summary.availableBalance}
      />
    </div>
  );
};
